import CloseIcon from "@assets/images/CloseModal.svg";
import SendMailIcon from "@assets/images/send-mail.svg";
import SendMailOkIcon from "@assets/images/sent-mail-success.svg";
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid
} from "@material-ui/core";
import { IListLine } from "@saleor/changeOrderDomestic/views/ViewEditOrder";
import MyTypography from "@saleor/components/MyTypography";
import { SingleTextField } from "@saleor/components/SingleTextField";
import TextEditor from "@saleor/components/TextEditor";
import TextInputDisable from "@saleor/components/TextInputDisable";
import {
  ContractChangeOrderDomesticQuery,
  useGetOrderEmailToAndCcQuery,
  useSendOrderEmailMutation
} from "@saleor/graphql";
import { useFormik } from "formik";
import moment from "moment/moment";
import React, { useState } from "react";
import SVG from "react-inlinesvg";
import { useIntl } from "react-intl";
import * as Yup from "yup";

import useStyles from "./styles";

export interface SendMailProps {
  open: boolean;
  orderId: string[];
  soldToCode: string[];
  soldToName: string;
  onClose: () => void;
  saleOrg?: string;
  contractNo?: string;
  dataChangeOrder?: ContractChangeOrderDomesticQuery;
  listLineOrder?: IListLine[];
}

const ModalSendMail: React.FC<SendMailProps> = props => {
  const {
    open,
    onClose,
    orderId,
    soldToCode,
    soldToName,
    saleOrg,
    contractNo,
    dataChangeOrder,
    listLineOrder
  } = props;
  const [openSuccess, setOpenSuccess] = useState<boolean>(false);
  const classes = useStyles();
  const intl = useIntl();

  const SendMailSchema = Yup.object().shape({
    to: Yup.string().required("Field To is required"),
    cc: Yup.string().required("Field Cc is required"),
    content: Yup.string().required("Field Content is required")
  });

  const { data: dataMail, loading } = useGetOrderEmailToAndCcQuery({
    variables: {
      bu: "PP",
      soldToCode,
      saleOrg: [saleOrg],
      soNo: orderId
    },
    skip: soldToCode.length === 0 || !open
  });

  const [sendMailOrder] = useSendOrderEmailMutation({
    onCompleted: data => {
      if (data?.sendOrderEmail?.errors.length === 0) {
        setOpenSuccess(true);
        onClose();
      }
    }
  });

  const formik = useFormik({
    initialValues: {
      to: dataMail?.getOrderEmailToAndCc?.to,
      cc: dataMail?.getOrderEmailToAndCc?.cc,
      subject: `Order Confirmation ${soldToName} ${moment().format(
        "DD/MM/YYYY"
      )}`,
      content:
        "<p>Dear Sir,</p>" +
        "<p>Please kindly find Order Confirmation as per file attached.</p>" +
        "<p></p>" +
        "<p>Best Regards,</p>" +
        "<p>Customer Services</p>" +
        "<p>SCGP</p>",
      orderId
    },
    enableReinitialize: true,
    onSubmit: () => {
      const itemNoList = dataChangeOrder.changeOrder.orderItems.map(
        item => item.itemNo
      );

      const soldToCode = dataChangeOrder.changeOrder.orderPartners.find(
        item => item.partnerRole === "AG"
      ).partnerNo;

      const shipToCode = dataChangeOrder.changeOrder.orderPartners.find(
        item => item.partnerRole === "WE"
      ).partnerNo;

      const orderLinesList = listLineOrder.map(item => {
        const formattedOrderQty = Number(item?.orderQty || 0).toFixed(3);
        const formattedConfirmQty = Number(item?.comfirmQty || 0).toFixed(3);
        const formattedNonConfirmQty = (
          Number(formattedOrderQty) - Number(formattedConfirmQty)
        ).toFixed(3);
        return {
          confirmQty: formattedConfirmQty,
          firstDeliveryDate: item.originalRequestDate,
          iplanConfirmDate: item.requestedDate,
          itemNo: item.itemNo,
          materialDescription: item.materialDesc,
          materialCode: item.material,
          orderQty: formattedOrderQty,
          saleUnit: item.salesUnit,
          soldTo: soldToCode,
          status: item.sapConfirmStatus,
          nonConfirmQty: formattedNonConfirmQty,
          remark: ""
        };
      });

      const data = {
        ...formik.values,
        orders: [
          {
            contractNo,
            contractName: "",
            createdDate: dataChangeOrder.changeOrder.orderDate,
            itemNo: itemNoList,
            orderLines: orderLinesList,
            paymentTermDesc: dataChangeOrder.changeOrder.paymentTerms,
            poNo: dataChangeOrder.changeOrder.poNo,
            saleOrg: dataChangeOrder.changeOrder.salesOrg,
            soNo: dataChangeOrder.changeOrder.soNo,
            shipTo: shipToCode
          }
        ]
      };
      onClose();
      sendMailOrder({
        variables: data
      });
    },
    validationSchema: SendMailSchema
  });

  return (
    <>
      <Dialog
        ata-testid="sendMail-modal"
        className={classes.root}
        open={open}
        onClose={onClose}
      >
        <DialogTitle className={classes.dialogTitle}>
          <div className={classes.flexCenter}>
            <SVG src={SendMailIcon} className={classes.icon} />
            <MyTypography variant={"h5"}>
              {intl.formatMessage({ defaultMessage: "Send Email" })}
            </MyTypography>
          </div>
          <SVG
            onClick={onClose}
            width={24}
            height={24}
            src={CloseIcon}
            className={classes.closeIcon}
          />
        </DialogTitle>
        <DialogContent>
          <Grid
            spacing={3}
            container
            alignItems={"center"}
            className={classes.gridBox}
          >
            <Grid xs={12} sm={2} item>
              <MyTypography variant="body3">
                {intl.formatMessage({ defaultMessage: "To" })}
                <span className={classes.required}>*</span>
              </MyTypography>
            </Grid>
            <Grid xs={12} sm={10} item>
              <SingleTextField
                name={"to"}
                value={formik.values.to}
                InputProps={{
                  inputProps: {
                    "data-testid": "to-input"
                  }
                }}
                onChange={formik.handleChange}
                error={!!formik.errors.to}
              />
            </Grid>
            <Grid xs={12} sm={2} item>
              <MyTypography variant="body3">
                {intl.formatMessage({ defaultMessage: "Cc" })}
                <span className={classes.required}>*</span>
              </MyTypography>
            </Grid>
            <Grid xs={12} sm={10} item>
              <SingleTextField
                name={"cc"}
                value={formik.values.cc}
                InputProps={{
                  inputProps: {
                    "data-testid": "cc-input"
                  }
                }}
                onChange={formik.handleChange}
                error={!!formik.errors.cc}
              />
            </Grid>
            <Grid xs={12} sm={2} item>
              <MyTypography variant="body3">
                {intl.formatMessage({ defaultMessage: "Subject" })}:
              </MyTypography>
            </Grid>
            <Grid xs={12} sm={10} item>
              <TextInputDisable
                value={formik.values.subject}
                testId="subject"
              />
            </Grid>
            <Grid sm={12} item>
              <div className={classes.contentBlock}>
                <MyTypography variant="body3" className={classes.contentTitle}>
                  {intl.formatMessage({ defaultMessage: "Message" })}
                  <span className={classes.required}>*</span>
                </MyTypography>
                <div className={classes.contentEditor}>
                  <TextEditor
                    name={"content"}
                    value={formik.values.content}
                    onChange={formik.handleChange}
                  />
                </div>
              </div>
            </Grid>
            <Grid sm={12} item className={classes.flexEnd}>
              <Button
                disabled={loading}
                type={"submit"}
                color="secondary"
                variant="contained"
                onClick={() => formik.submitForm()}
              >
                <MyTypography variant="body1">
                  {intl.formatMessage({ defaultMessage: "Send" })}
                </MyTypography>
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
      <Dialog open={openSuccess}>
        <DialogContent className={classes.boxSuccess}>
          <div className={classes.flexCenter}>
            <SVG src={SendMailOkIcon} className={classes.icon} />
            <MyTypography variant={"h6"}>
              {intl.formatMessage({ defaultMessage: "ส่งอีเมล สำเร็จ" })}
            </MyTypography>
          </div>
          <MyTypography variant={"body2"}>
            {intl.formatMessage({
              defaultMessage: "ระบบ eOrdering ได้ส่งอีเมลของคุณเรียบร้อยแล้ว"
            })}
          </MyTypography>
          <Button
            fullWidth
            color="secondary"
            variant="contained"
            onClick={() => setOpenSuccess(false)}
          >
            <MyTypography variant="body1">
              {intl.formatMessage({ defaultMessage: "ปิดหน้าต่าง" })}
            </MyTypography>
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ModalSendMail;
